import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

function ScoreboardArea({ players, currentPlayerId }) {
  const theme = useTheme();
  const sorted = [...players].sort((a, b) => b.score - a.score);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.25,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.6),
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}>
        <EmojiEventsIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Scoreboard</Typography>
      </Box>

      {/* Player list */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <List dense sx={{ p: 0.5 }}>
          {sorted.map((p, i) => (
            <ListItem
              key={p.id}
              sx={{
                px: 1.5,
                py: 1,
                mb: 0.5,
                borderRadius: 1.5,
                backgroundColor: p.id === currentPlayerId
                  ? alpha(theme.palette.primary.main, 0.08)
                  : i === 0 && p.score > 0
                    ? alpha(medalColors[0], 0.08)
                    : "transparent",
                border: p.id === currentPlayerId
                  ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  : "1px solid transparent",
                transition: "background-color 0.3s ease",
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
              }}
            >
              {/* Rank */}
              <Box sx={{ width: 24, display: "flex", justifyContent: "center", mr: 1, flexShrink: 0 }}>
                {i < 3 && p.score > 0 ? (
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: medalColors[i] }}>{i + 1}</Typography>
                ) : (
                  <Typography variant="caption" sx={{ opacity: 0.4, fontWeight: 600 }}>{i + 1}</Typography>
                )}
              </Box>

              {/* Avatar */}
              <Box sx={{ position: "relative", mr: 1.5, flexShrink: 0 }}>
                <Avatar sx={{
                  width: 30, height: 30, fontSize: "0.7rem", fontWeight: 700,
                  bgcolor: i < 3 && p.score > 0 ? medalColors[i] : theme.palette.primary.main,
                  color: i < 3 && p.score > 0 ? "#000" : "#fff",
                }}>
                  {p.username.charAt(0).toUpperCase()}
                </Avatar>
                <FiberManualRecordIcon sx={{
                  position: "absolute", bottom: -1, right: -1,
                  fontSize: 8,
                  color: p.connected ? theme.palette.success.main : theme.palette.error.main,
                }} />
              </Box>

              {/* Name */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.username}
                  {p.isHost && (
                    <Box component="span" sx={{ ml: 0.5, fontSize: "0.6rem", opacity: 0.5 }}>HOST</Box>
                  )}
                  {p.isBot && (
                    <Box component="span" sx={{ ml: 0.5, fontSize: "0.55rem", fontWeight: 800, px: 0.5, py: 0.1, borderRadius: 0.5, backgroundColor: alpha(theme.palette.info.main, 0.15), color: theme.palette.info.main }}>BOT</Box>
                  )}
                  {p.id === currentPlayerId && (
                    <Box component="span" sx={{ ml: 0.5, fontSize: "0.6rem", color: theme.palette.primary.main, fontWeight: 700, opacity: 0.8 }}>(You)</Box>
                  )}
                </Typography>
              </Box>

              {/* Score */}
              <Typography
                className="score-value"
                variant="body2"
                sx={{
                  fontWeight: 800,
                  fontSize: "0.9rem",
                  color: i === 0 && p.score > 0 ? medalColors[0] : theme.palette.text.primary,
                  ml: 1,
                  flexShrink: 0,
                }}
              >
                {p.score}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
}

export default ScoreboardArea;
